/* eslint-disable max-lines */
import { Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import {
  ImageUploadedBanner,
  useArtwork,
  useArtworkForm,
  useArtworkImage,
  useEnhancedImageSource,
  usePlacementPosition
} from "@design-lab/entities/artwork";
import { useProductColors } from "@design-lab/entities/product";
import { isUserLoggedIn } from "@design-lab/entities/user";
import { useDesignProjectSync } from "@design-lab/features/design-project/model/useDesignProjectSync";
import { ImageSelector, type ImageSelectorRef } from "@design-lab/features/upload-artwork";
import { toFileObject, type Side } from "@design-lab/shared";
import { ARTWORK_PROCESSING } from "@design-lab/shared/constants";
import { validateDesignLabArtworkFile } from "common/artwork-file-types";
import { notifyError } from "common/utils/app-monitoring/notify";
import { useNotification } from "@design-lab/shared/ui/notification";
import { Button, LoadingBar } from "@design-lab/shared/ui";
import { StartDesigningModal } from "./StartDesigningModal";
import { useDesignLibrarySelection } from "../model/useDesignLibrarySelection";
import { isClientError } from "../lib/isClientError";

export type DesignLabImageSelectorProps = {
  side: Side;
};

/**
 * Renders the image selection UI and related modals for the specified side of a shirt.
 *
 * @param side - The shirt side this selector manages (e.g., "front" or "back").
 * @returns The React element that displays the image selector, uploaded-image banner, loading state, and start-designing modal for the given side.
 */
export function DesignLabImageSelector({ side }: DesignLabImageSelectorProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { uploadArtwork, deleteArtwork } = useArtworkImage();
  const { getMachineBySide } = useArtwork();
  const machine = getMachineBySide(side);
  const { artworkId, source } = useEnhancedImageSource({ side });
  const { error: notifyUserError } = useNotification();
  const isLoggedIn = isUserLoggedIn();
  const isBack = side === "back";
  const { currentProduct } = useProductColors();

  const { lens } = useArtworkForm();
  const { placement: selectedFrontPlacement } = usePlacementPosition(
    lens.focus("selectedFrontPlacement")
  );
  const placement = side === "back" ? side : selectedFrontPlacement;

  const onArtworkRemove = useCallback(() => {
    deleteArtwork(side, artworkId);
  }, [deleteArtwork, side, artworkId]);

  const handleFileDrop = async (droppedFiles: File[]) => {
    const file = droppedFiles[0];
    if (!file) return;

    const { valid, message, file: materializedFile } = await validateDesignLabArtworkFile(file);
    if (!valid) {
      notifyUserError({ title: "Upload Failed", description: message });
      return;
    }

    // Use the materialized file for upload (handles cloud storage providers on mobile)
    const fileToUpload = materializedFile || file;

    try {
      await uploadArtwork({ file: fileToUpload, side });
    } catch (error) {
      const errorData = (error as any)?.response?.data;
      const errorMessage =
        errorData?.validation_error ||
        errorData?.error ||
        "Failed to upload file. Please try again.";

      if (!isClientError(error)) {
        notifyError("DesignLabImageSelector handleFileDrop error", {
          params: {
            error: error instanceof Error ? error.message : String(error),
            error_object: error instanceof Error ? error.toString() : String(error),
            stack_trace: error instanceof Error ? error.stack : null,
            component: "DesignLabImageSelector",
            action: "handleFileDrop",
            side
          }
        });
      }

      notifyUserError({
        title: "Upload Failed",
        description: errorMessage
      });
    }
  };

  const {
    showModal,
    isWaitingForLibrary,
    isWaitingForCanva,
    targetSide,
    handleLibrarySelection,
    continueWithLatest,
    startNew,
    selectFiles
  } = useDesignProjectSync();
  const [shouldOpenLibrary, setShouldOpenLibrary] = useState(false);
  const [shouldOpenCanvaFromParent, setShouldOpenCanvaFromParent] = useState(false);

  // Open other modals only if Start Designing is not active
  // Only respond if this component's side matches the targetSide from the workflow
  const shouldRespondToWorkflow = targetSide === side;

  useEffect(() => {
    // Use the machine state to determine when to open modals
    if (isWaitingForLibrary && isLoggedIn && shouldRespondToWorkflow) {
      setShouldOpenLibrary(true);
    } else if (isWaitingForCanva && isLoggedIn && shouldRespondToWorkflow) {
      setShouldOpenCanvaFromParent(true);
    } else if (!isWaitingForLibrary && !isWaitingForCanva) {
      // Machine is not waiting for library/canva anymore, close modals
      setShouldOpenLibrary(false);
      setShouldOpenCanvaFromParent(false);
    }
  }, [isWaitingForLibrary, isWaitingForCanva, isLoggedIn, shouldRespondToWorkflow]);

  const handleQueryParams = useCallback(() => {
    const choiceParam = searchParams.get("choice");
    const hasDesignLibraryParam = choiceParam === "Library";
    const hasCanvaParam = choiceParam === "Canva";
    const hasAutoUploadParams =
      searchParams.get("artwork_urls") && searchParams.get("file_content_type");
    const hasCatalogNumber = searchParams.get("catalog_number");
    const hasAc = searchParams.get("ac");

    if (hasAutoUploadParams || (hasCatalogNumber && hasAc)) return;

    // Check if this component should respond based on the side parameter
    const sideParam = searchParams.get("side");
    const shouldRespond = !sideParam || sideParam === side;

    if (hasDesignLibraryParam && isLoggedIn && shouldRespond) {
      setShouldOpenLibrary(true);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("choice");
      setSearchParams(newParams, { replace: true });
    } else if (hasCanvaParam && isLoggedIn && shouldRespond) {
      setShouldOpenCanvaFromParent(true);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("choice");
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams, isLoggedIn, side]);

  useEffect(() => {
    handleQueryParams();
  }, [handleQueryParams]);

  const onCanvaAutoOpenCompleteFromParent = useCallback(() => {
    setShouldOpenCanvaFromParent(false);
  }, []);

  // Create ref for ImageSelector
  const imageSelectorRef = useRef<ImageSelectorRef>(null);

  const onLibraryAutoOpenComplete = useCallback(() => {
    setShouldOpenLibrary(false);
    // Notify machine if it was waiting for library
    if (isWaitingForLibrary && handleLibrarySelection) {
      // If the library was closed without selection, cancel the workflow
      handleLibrarySelection();
    }
  }, [isWaitingForLibrary, handleLibrarySelection]);

  // Canva file handler
  const handleCanvaFileReady = useCallback(
    async (file: File) => {
      try {
        await uploadArtwork({ file, side, source: "canva" });
      } catch (error) {
        const errorData = (error as any)?.response?.data;
        const errorMessage =
          errorData?.validation_error ||
          errorData?.error ||
          "Failed to upload file from Canva. Please try again.";

        if (!isClientError(error)) {
          notifyError("DesignLabImageSelector handleCanvaFileReady error", {
            params: {
              error: error instanceof Error ? error.message : String(error),
              error_object: error instanceof Error ? error.toString() : String(error),
              stack_trace: error instanceof Error ? error.stack : null,
              component: "DesignLabImageSelector",
              action: "handleCanvaFileReady",
              side
            }
          });
        }

        notifyUserError({
          title: "Upload Failed",
          description: errorMessage
        });
      }
    },
    [uploadArtwork, side, notifyUserError]
  );

  // My Design Library handler
  const { selectArtwork } = useDesignLibrarySelection({
    placement,
    onSuccess: useCallback(() => {
      // Library will close itself after successful selection
      // Notify the machine that library selection is complete
      if (isWaitingForLibrary && handleLibrarySelection) {
        handleLibrarySelection();
      }
      // Ensure modal state is cleared
      setShouldOpenLibrary(false);
    }, [isWaitingForLibrary, handleLibrarySelection])
  });

  const isFileUploaded = Boolean(machine.state.context.artworkId);
  const { file, originalImageUrl } = machine.state.context;
  const isStartDesigningShown = showModal && !isBack;

  // Handle different rendering states
  if (!isStartDesigningShown && machine.state.matches("finished") && source.enhancedImage) {
    return <ImageUploadedBanner image={{ url: source.enhancedImage.base64 }} side={side} />;
  }

  if (!isStartDesigningShown && isFileUploaded && file) {
    return <ImageUploadedBanner image={toFileObject(file, originalImageUrl)} side={side} />;
  }

  if (!isStartDesigningShown && machine.state.hasTag("loading")) {
    return (
      <div className="flex flex-1 flex-col gap-2">
        <LoadingBar
          side={side}
          startTime={machine.state.context.startTime}
          estimatedDuration={ARTWORK_PROCESSING.ESTIMATED_DURATION}
        />
        {isFileUploaded && (
          <div className="flex justify-center">
            <Button
              data-testid="delete-image-button"
              variant="outline"
              size="sm"
              onPress={onArtworkRemove}
            >
              <Trash2 className="size-4 text-red-600" />
              Delete image
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <ImageSelector
        ref={imageSelectorRef}
        side={side}
        title={
          <>
            <span className="capitalize">{side}</span> of Shirt
          </>
        }
        onFileDrop={handleFileDrop}
        isLoggedIn={isLoggedIn}
        shouldOpenLibrary={shouldOpenLibrary}
        onLibraryAutoOpenComplete={onLibraryAutoOpenComplete}
        shouldOpenCanvaFromParent={shouldOpenCanvaFromParent}
        onCanvaAutoOpenCompleteFromParent={onCanvaAutoOpenCompleteFromParent}
        onCanvaFileReady={handleCanvaFileReady}
        onLibraryDesignSelect={selectArtwork}
        isWaitingForCatalogNumber={!currentProduct?.catalogNumber}
      />
      {isStartDesigningShown && (
        <StartDesigningModal
          isOpen={showModal}
          isLoggedIn={isLoggedIn}
          onDropFiles={(files: File[]) => {
            // Start new project with device source, then upload files
            startNew("device");
            selectFiles(files);
            handleFileDrop(files);
          }}
          onClose={continueWithLatest}
          onChooseDevice={() => {
            startNew("device");
            imageSelectorRef.current?.openDevice();
          }}
          onChooseLibrary={() => {
            startNew("library");
            imageSelectorRef.current?.openLibrary();
          }}
          onChooseCanva={() => {
            startNew("canva");
            imageSelectorRef.current?.openCanva();
          }}
        />
      )}
    </>
  );
}
